<script>
import { FacilityType } from "@/data/facility";
import ValidationMixin from "@/mixins/validation.mixin";
import LocationPicker from "@/components/pickers/LocationPicker.vue";
import CoordinatesField from "@/views/companies/facilities/components/CoordinatesField.vue";
import LocationField from "@/views/companies/facilities/components/LocationField.vue";
import OmniTooltip from "@/components/OmniTooltip.vue";

export default {
  name: "FacilityAddressForm",
  components: { OmniTooltip, LocationField, CoordinatesField, LocationPicker },
  mixins: [ValidationMixin],
  props: {
    modelValue: null,
    modal: Boolean,
  },
  emits: ["update:modelValue"],
  data() {
    return {
      location: null,
    };
  },
  computed: {
    facility: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    isStandard() {
      return this.facility.type === FacilityType.standard;
    },
    isPoint() {
      return this.facility.type === FacilityType.point;
    },
    isToBeDiscussed() {
      return this.facility.type === FacilityType.toBeDiscussed;
    },
    isManual() {
      return this.facility.type === FacilityType.manual;
    },
    hasCreated() {
      return Boolean(this.facility.id);
    },
  },
  watch: {
    location() {
      this.facility.address = this.location.fullAddress;
      this.facility.addressCoordinates = { lat: this.location.lat, lon: this.location.lon };
    },
    modelValue(value) {
      if (value.address && !this.location) {
        this.location = {
          fullAddress: value.address,
          lat: value.addressCoordinates.lat,
          lon: value.addressCoordinates.lon,
        };
      }
    },
  },
};
</script>

<template>
  <v-row v-if="isStandard">
    <v-col lg="6">
      <omni-tooltip :disabled="!hasCreated">
        <template #activator>
          <v-text-field
            v-model="facility.addressLine"
            :disabled="hasCreated"
            :rules="[requiredValidator]"
            autocomplete="off1"
            class="required"
            color="primary"
            data-qa="address-line"
            density="compact"
            hide-details="auto"
            label="Address line"
            variant="outlined"
          />
        </template>

        Facility address cannot be edited
      </omni-tooltip>
    </v-col>
    <v-col lg="6">
      <div class="d-flex align-center">
        <omni-tooltip :disabled="!hasCreated">
          <template #activator>
            <location-picker
              v-model="location"
              :disabled="hasCreated"
              class="flex-1"
              hide-coordinates
              qa-attr="location"
              required
            ></location-picker>
          </template>

          Facility location cannot be edited
        </omni-tooltip>
        <v-tooltip location="top end">
          <template #activator="{ props }">
            <v-icon class="ml-3" color="primary" icon="mdi-information-outline" v-bind="props"></v-icon>
          </template>
          Loaded miles will be calculated based on this field
        </v-tooltip>
      </div>
    </v-col>
  </v-row>
  <v-row v-if="isPoint">
    <v-col lg="6">
      <omni-tooltip :disabled="!hasCreated">
        <template #activator>
          <coordinates-field
            v-model="facility.preciseCoordinates"
            :disabled="hasCreated"
            data-qa="coordinates"
            required
          ></coordinates-field>
        </template>

        Facility coordinates cannot be edited
      </omni-tooltip>
    </v-col>
    <v-col lg="6">
      <div class="d-flex align-center">
        <omni-tooltip :disabled="!hasCreated">
          <template #activator>
            <location-picker
              v-model="location"
              :disabled="hasCreated"
              class="flex-1"
              hide-coordinates
              qa-attr="location"
              required
            ></location-picker>
          </template>

          Facility location cannot be edited
        </omni-tooltip>
        <v-tooltip location="top end">
          <template #activator="{ props }">
            <v-icon class="ml-3" color="primary" icon="mdi-information-outline" v-bind="props"></v-icon>
          </template>
          Loaded miles will be calculated based on this field
        </v-tooltip>
      </div>
    </v-col>
  </v-row>
  <v-row v-if="isToBeDiscussed">
    <v-col>
      <div class="d-flex align-center">
        <omni-tooltip :disabled="!hasCreated">
          <template #activator>
            <location-picker
              v-model="location"
              :disabled="hasCreated"
              class="flex-1"
              hide-coordinates
              qa-attr="location"
              required
            ></location-picker>
          </template>

          Facility location cannot be edited
        </omni-tooltip>
        <v-tooltip location="top end">
          <template #activator="{ props }">
            <v-icon class="ml-3" color="primary" icon="mdi-information-outline" v-bind="props"></v-icon>
          </template>
          Loaded miles will be calculated based on this field
        </v-tooltip>
      </div>
    </v-col>
  </v-row>
  <v-row v-if="isManual">
    <v-col :lg="modal ? 6 : 4">
      <omni-tooltip :disabled="!hasCreated">
        <template #activator>
          <v-text-field
            v-model="facility.addressLine"
            :disabled="hasCreated"
            :rules="[requiredValidator]"
            autocomplete="off1"
            class="required"
            color="primary"
            data-qa="address-line"
            density="compact"
            hide-details="auto"
            label="Address line"
            variant="outlined"
          />
        </template>

        Facility address line cannot be edited
      </omni-tooltip>
    </v-col>
    <v-col :lg="modal ? 6 : 4">
      <omni-tooltip :disabled="!hasCreated">
        <template #activator>
          <location-field v-model="facility.address" :disabled="hasCreated" data-qa="location" required />
        </template>

        Facility location cannot be edited
      </omni-tooltip>
    </v-col>
    <v-col :lg="modal ? 12 : 4">
      <div class="d-flex align-center">
        <div class="w-100">
          <omni-tooltip :disabled="!hasCreated">
            <template #activator>
              <coordinates-field
                v-model="facility.addressCoordinates"
                :disabled="hasCreated"
                class="w-100"
                data-qa="coordinates"
                required
              ></coordinates-field>
            </template>

            Facility coordinates cannot be edited
          </omni-tooltip>
        </div>

        <v-tooltip location="top end">
          <template #activator="{ props }">
            <v-icon class="ml-3" color="primary" icon="mdi-information-outline" v-bind="props"></v-icon>
          </template>
          Loaded miles will be calculated based on this field
        </v-tooltip>
      </div>
    </v-col>
  </v-row>
</template>

<style lang="scss" scoped></style>
